import React, { useCallback } from 'react';
import Menu, { MenuProps } from '@mui/material/Menu';
import Box from 'fe-design-base/atoms/Box';

import { cxHelpers } from 'util/className';
const { cx, cxEl } = cxHelpers('FDBContextMenu');
import MenuDivider from './MenuDivider';
import NestedMenu from './NestedMenu';
import SimpleMenuItem from './SimpleMenuItem';
import { MenuItemSections } from './types';

export const VERTICAL_POS = ['top', 'center', 'bottom'] as const;
export const HORIZONTAL_POS = ['left', 'center', 'right'] as const;

type OriginVertical = (typeof VERTICAL_POS)[number];
type OriginHorizontal = (typeof HORIZONTAL_POS)[number];

interface ContextMenuProps extends MenuProps {
  /**
   * Array of menu items sections to be displayed
   * If array is not passed, children ContextMenuItems should be used instead
   * */
  menuItemSections?: MenuItemSections;
  anchorOriginVertical?: OriginVertical;
  anchorOriginHorizontal?: OriginHorizontal;
  transformOriginVertical?: OriginVertical;
  transformOriginHorizontal?: OriginHorizontal;
  disableScrollLock?: boolean;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  menuItemSections,
  children,
  open,
  // currently this is what is wanted for default anchor/transform behavior
  anchorOriginVertical = 'bottom',
  anchorOriginHorizontal = 'left',
  transformOriginVertical = 'top',
  transformOriginHorizontal = 'left',
  anchorEl,
  disableScrollLock,
  onClose,
}) => {
  const anchorOrigin = {
    vertical: anchorOriginVertical,
    horizontal: anchorOriginHorizontal,
  };
  const transformOrigin = {
    vertical: transformOriginVertical,
    horizontal: transformOriginHorizontal,
  };

  // Prevents propagation bugs when clicking on a divider or white space between menu items
  const handleClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
  }, []);

  if ((!menuItemSections || !menuItemSections.length) && !children)
    return <></>;
  return (
    <Menu
      className={cx()}
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      onClose={onClose}
      onClick={handleClick}
      disableScrollLock={disableScrollLock}
      sx={{
        maxHeight: disableScrollLock ? 800 : undefined,
        position: disableScrollLock ? 'absolute' : undefined,
      }}
    >
      {typeof menuItemSections === 'undefined' && children}
      {menuItemSections &&
        menuItemSections.length === 1 &&
        menuItemSections
          .flat()
          .map(item =>
            item.subMenuItemSections ? (
              <NestedMenu
                className={cxEl('Nested-Menu')}
                key={item.label}
                parentMenuOpen={open}
                iconName={item.iconName}
                label={item.label}
                subMenuItemSections={item.subMenuItemSections}
                disabled={item.disabled}
              />
            ) : (
              <SimpleMenuItem
                key={item.label}
                {...item}
                className={cxEl('Menu-Item')}
              />
            )
          )}
      {menuItemSections &&
        menuItemSections.length > 1 &&
        menuItemSections.map((section, index) => (
          <Box key={index}>
            {section.map(item =>
              item.subMenuItemSections ? (
                <NestedMenu
                  className={cxEl('Nested-Menu')}
                  key={item.label}
                  parentMenuOpen={open}
                  iconName={item.iconName}
                  label={item.label}
                  subMenuItemSections={item.subMenuItemSections}
                  disabled={item.disabled}
                />
              ) : (
                <SimpleMenuItem
                  key={item.label}
                  {...item}
                  className={cxEl('Menu-Item')}
                  sx={{
                    maxHeight: disableScrollLock ? 800 : undefined,
                    position: disableScrollLock ? 'absolute' : undefined,
                  }}
                />
              )
            )}
            {index !== menuItemSections.length - 1 && <MenuDivider />}
          </Box>
        ))}
    </Menu>
  );
};

export default ContextMenu;
